import { Link } from "react-router-dom";
import { Phone, Play } from "lucide-react";

export const heure = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }).replace(":", " h ");
export const duree = (s) => { const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); return h ? `${h} h ${String(m).padStart(2, "0")}` : `${m} min`; };

export const Carte = ({ titre, droite, testId, className = "", children }) => (
  <section className={`at-carte min-w-0 ${className}`} data-testid={testId}>
    {titre && (
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-sm font-medium truncate">{titre}</h2>
        {droite && <span className="text-xs at-muted shrink-0">{droite}</span>}
      </div>
    )}
    {children}
  </section>
);

const Courbe = ({ serie, couleur }) => {
  if (!serie?.length || Math.max(...serie) === 0) return <span className="w-24 h-8" />;
  const max = Math.max(...serie);
  const pts = serie.map((v, i) => `${(i / (serie.length - 1)) * 96},${30 - (v / max) * 28}`).join(" ");
  return <svg width="96" height="32" viewBox="0 0 96 32" aria-hidden="true" className="shrink-0"><polyline points={pts} fill="none" stroke={couleur} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
};

export const Compteur = ({ titre, valeur, sur, pct, couleur, serie, note, testId, delai }) => (
  <section className="at-carte min-w-0 at-rise" style={{ animationDelay: `${delai}ms` }} data-testid={testId}>
    <p className="text-sm font-medium">{titre}</p>
    <div className="flex items-end justify-between gap-2 mt-2">
      <p className="text-[32px] leading-none font-medium tabular-nums" data-testid={`${testId}-valeur`}>{valeur}{sur && <span className="text-sm at-muted font-semibold"> {sur}</span>}</p>
      <Courbe serie={serie} couleur={couleur} />
    </div>
    <div className="h-1.5 at-pill mt-3 overflow-hidden" style={{ background: "var(--surface-3, var(--surface-2))" }}>
      <span className="block h-full at-pill" style={{ width: `${Math.min(100, pct || 0)}%`, background: couleur, transition: "width .6s cubic-bezier(.23,1,.32,1)" }} />
    </div>
    <p className="text-xs at-muted mt-2 truncate">{note}</p>
  </section>
);

const Ligne = ({ p }) => (
  <Link to={`/focus?prospect=${p.id}`} className="flex items-center justify-between gap-2 min-h-[44px] text-sm" data-testid={`next-after-${p.id}`}>
    <span className="truncate"><b>{p.prenom} {p.nom}</b> <span className="at-muted">· {p.ville}</span></span>
    <span className="text-xs at-muted shrink-0">{p.statut_nom}</span>
  </Link>
);

export const ProchaineFiche = ({ prochaine, suivants }) => {
  if (!prochaine) return <Carte titre="⏭️ Prochaine fiche" testId="next-card" className="lg:col-span-5"><p className="at-muted text-sm" data-testid="next-card-empty">Votre liste est vide pour le moment.</p></Carte>;
  const p = prochaine.prospect;
  return (
    <Carte titre="⏭️ Prochaine fiche" droite={prochaine.raison} testId="next-card" className="lg:col-span-5">
      <p className="text-xl font-bold truncate" data-testid="next-card-name">{p.civilite} {p.prenom} {p.nom}</p>
      <p className="at-muted text-sm truncate">{p.ville} ({p.departement}) · Fichier de prospection</p>
      <div className="flex flex-wrap gap-2 mt-3">
        <span className="at-etiquette" style={{ background: `color-mix(in srgb, ${p.statut_couleur} 16%, var(--surface))` }}>{p.statut_nom}</span>
        <span className="at-etiquette">Tentative {p.tentatives + 1}</span>
        {p.derniere_tentative && <span className="at-etiquette">Dernier appel {new Date(p.derniere_tentative).toLocaleDateString("fr-FR")}</span>}
      </div>
      {prochaine.preparation && (
        <div className="mt-4 text-sm at-bulle-ia" data-testid="next-card-tip">
          <p className="text-xs font-semibold at-ia">💡 Conseil de l'assistant</p>
          <p className="mt-1">{prochaine.preparation}</p>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-4">
        <Link to={`/focus?prospect=${p.id}`} className="at-btn at-btn-primary" data-testid="next-card-call"><Phone size={16} />Appeler maintenant</Link>
        {p.fiche_ids?.[0] && <Link to={`/fiches/${p.fiche_ids[0]}`} className="at-btn at-btn-ghost" data-testid="next-card-open">Voir la fiche</Link>}
      </div>
      {suivants.length > 0 && <div className="mt-4 pt-3 border-t at-sep"><p className="text-xs at-muted font-semibold mb-1">Ensuite</p>{suivants.map((s) => <Ligne key={s.id} p={s} />)}</div>}
    </Carte>
  );
};

export const MesFichiers = ({ fichiers, statuts }) => {
  const total = statuts.reduce((s, x) => s + x.nombre, 0);
  return (
    <Carte titre="📂 Mes fichiers de prospection" droite={`${fichiers.length} fichier${fichiers.length > 1 ? "s" : ""}`} testId="files-card" className="lg:col-span-5">
      {total > 0 && <div className="flex h-2.5 at-pill overflow-hidden mb-2" style={{ background: "var(--surface-3, var(--surface-2))" }}>{statuts.map((s) => <span key={s.code} style={{ flex: s.nombre, background: s.couleur }} title={`${s.nom} : ${s.nombre}`} />)}</div>}
      {total > 0 && <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mb-4">{statuts.map((s) => <span key={s.code} className="flex items-center gap-1.5"><span className="w-2 h-2 at-pill" style={{ background: s.couleur }} />{s.nom} <b>{s.nombre}</b></span>)}</div>}
      {fichiers.length === 0 && <p className="at-muted text-sm">Aucun fichier attribué.</p>}
      <div className="space-y-1">
        {fichiers.map((f) => (
          <div key={f.id || "aucun"} className="flex items-center gap-3 min-h-[52px]" data-testid={`file-row-${f.id}`}>
            <span className="text-lg" aria-hidden="true">📂</span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm truncate">{f.nom}</p>
              <div className="h-1.5 at-pill mt-1.5 overflow-hidden" style={{ background: "var(--surface-3, var(--surface-2))" }}><span className="block h-full at-pill" style={{ width: `${f.total ? Math.round(100 * (f.total - f.restants) / f.total) : 0}%`, background: "var(--accent)" }} /></div>
            </div>
            <span className="text-xs at-muted shrink-0 tabular-nums">{f.restants} rest.</span>
            <Link to="/appels" aria-label={`Appeler ${f.nom}`} className="at-icon-btn shrink-0" data-testid={`file-start-${f.id}`}><Play size={16} /></Link>
          </div>
        ))}
      </div>
    </Carte>
  );
};
